import { AirportFacet, Maybe, NightFacet, Receipt, SaleCalendar } from '@gql/schema';
import { ITranslationsObject, i18n } from '@secretescapes/i18n';
import { formatMomentToString } from '@utils/dates.utils';
import { minBy } from '@utils/lodash.utils';
import { numberToCurrency } from '@utils/pricing.utils';
import { ADULTS_ONLY_TAG } from '@utils/tags.config';
import moment from 'moment/moment';
import {
  deleteSearchParam,
  getParam,
  hasSearchParam,
  navigateReplace,
  setSearchParamValue,
} from 'src/app/utils/url.utils';
import { URLParams } from './AvailabilityPresentation.interfaces';
import { Option } from './AvailabilitySearchBar/DropdownInput';
import { CalendarURLParams } from './Calendar/Calendar.interfaces';
import { ExtendedOffer } from './Offers';

export const CALENDAR_DAY_DIMENSIONS_PX = 48;
export const ALL_OFFERS = 'all';
export const ALL_DURATIONS = 'all';

/**
 * Calendar should not go past last check-out date,
 * else not go past last available check-in date
 */
export const getLastDatesToShow = (
  availability: Maybe<SaleCalendar>,
  lastCheckOutDate: Maybe<string>
) => {
  return shouldUseLastCheckOutDate(availability, lastCheckOutDate)
    ? moment(lastCheckOutDate)
    : availability?.lastAvailableCheckInDate
      ? moment(availability?.lastAvailableCheckInDate)
      : moment();
};

export const shouldUseLastCheckOutDate = (
  availability: Maybe<SaleCalendar>,
  lastCheckOutDate: Maybe<string>
) => {
  return (
    !!lastCheckOutDate && moment(lastCheckOutDate) > moment(availability?.lastAvailableCheckInDate)
  );
};

export const setCalendarURLParams = ({ startDate, endDate }: CalendarURLParams) => {
  if (!startDate && !endDate) {
    navigateReplace(deleteSearchParam('checkin', 'checkout'));
    return;
  }

  const formattedStartDate = formatMomentToString(startDate);
  const formattedEndDate = formatMomentToString(endDate);

  if (formattedStartDate) {
    const checkInDateUrl = setSearchParamValue('checkin', formattedStartDate);

    if (!formattedEndDate) {
      checkInDateUrl.searchParams.delete('checkout');
    }

    navigateReplace(checkInDateUrl);
  }

  if (formattedEndDate) {
    const checkOutDateUrl = setSearchParamValue('checkout', formattedEndDate);
    navigateReplace(checkOutDateUrl);
  }
};

export const getFinalRates = (
  receipt: Maybe<Receipt>,
  numberOfRooms: number,
  currency: string,
  territoryLocale: string
) => {
  if (!receipt?.amount || !receipt.nights) {
    return undefined;
  }

  const finalRatePerRoomPerNight = numberToCurrency(
    Math.ceil(receipt.amount / (numberOfRooms * receipt.nights)),
    currency,
    territoryLocale,
    true
  );

  return {
    finalRate: receipt.amountForDisplay ?? '',
    finalRatePerRoomPerNight,
  };
};

export const getCheapestFinalRates = (
  receipts: Receipt[],
  numOfRooms: number,
  currency: string,
  territoryLocale: string
) => {
  if (receipts.length === 0) {
    return undefined;
  }

  const cheapestReceipt = minBy(receipts, receipt => receipt.amount);

  if (!cheapestReceipt?.amount || !cheapestReceipt.nights) {
    return undefined;
  }

  const cheapestFinalRatePerRoomPerNight = numberToCurrency(
    Math.ceil(cheapestReceipt.amount / (numOfRooms * cheapestReceipt.nights)),
    currency,
    territoryLocale,
    true
  );

  return {
    cheapestFinalRate: cheapestReceipt.amountForDisplay ?? '',
    cheapestFinalRatePerRoomPerNight,
  };
};

export function notEmpty<T>(value: Maybe<T>): value is T {
  return !!value;
}

export const determineMaxRoomCapacity = (
  offers: ExtendedOffer[],
  saleTags: Maybe<Maybe<string>[]>
) => {
  if (!offers?.length) {
    return {
      rooms: 0,
      isAdultsOnly: saleTags?.includes(ADULTS_ONLY_TAG),
    };
  }

  return {
    rooms: Math.max(...offers.map(offer => offer.pricing?.maxAvailability ?? 0)),
    isAdultsOnly: saleTags?.includes(ADULTS_ONLY_TAG),
  };
};

export const getURLParams = (): URLParams => {
  return {
    tripTypeParam: getParam('tripType'),
    airportCodeParam: getParam('airportCode'),
    durationParam: getParam('duration'),
    airportNameParam: getParam('airportName'),
  };
};

export const urlParamsExist = () => {
  return hasSearchParam('airportCode', 'duration', 'airportName');
};

export const deleteURLParams = () => {
  const updatedUrl = deleteSearchParam('airportCode', 'duration', 'airportName');

  navigateReplace(updatedUrl);
};

export const getAirportOptions = (
  data: Maybe<AirportFacet[]>,
  translations: ITranslationsObject
): Option[] => {
  return (data ?? [])
    .filter(entry => entry.amount)
    .map(entry => ({
      value: entry.code ?? '',
      text: entry.name ?? '',
      subtext: `${i18n('common.fromPrice.label', [], translations)} ${entry.amountForDisplay}`,
    }));
};

export const getNightOptions = (
  data: Maybe<NightFacet[]>,
  translations: ITranslationsObject
): Option[] => {
  return (data ?? [])
    .filter(entry => entry.amount)
    .map(entry => ({
      value: entry.nights?.toString() ?? ALL_DURATIONS,
      text: entry.nights
        ? i18n('common.nights.text', [entry.nights], translations)
        : i18n('flights.all.durations', [], translations),
      subtext: `${i18n('common.fromPrice.label', [], translations)} ${entry.amountForDisplay}`,
    }));
};
